import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./WorkspaceUsers.css";

const WorkspaceUsers = () => {
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [dailyInteractionData, setDailyInteractionData] = useState(null);
	const [timeRange, setTimeRange] = useState("this-month");
	const [loadingEngagement, setLoadingEngagement] = useState(false);
	// Per-workspace transcripts chart (mirrors the interactions chart)
	const [transcriptData, setTranscriptData] = useState(null);
	const [transcriptRange, setTranscriptRange] = useState("this-month");
	const [loadingTranscripts, setLoadingTranscripts] = useState(false);
	// Per-workspace copilot chats chart (mirrors the transcripts chart)
	const [copilotData, setCopilotData] = useState(null);
	const [copilotRange, setCopilotRange] = useState("this-month");
	const [loadingCopilot, setLoadingCopilot] = useState(false);
	const [hourlyData, setHourlyData] = useState([]);
	const [loadingHourlyData, setLoadingHourlyData] = useState(true);
	const [timeInterval, setTimeInterval] = useState("30min");
	const [searchTerm, setSearchTerm] = useState("");
	const [sortConfig, setSortConfig] = useState({
		key: null,
		direction: "asc",
	});
	const [billingOptedOut, setBillingOptedOut] = useState(false);
	const [billingUpdating, setBillingUpdating] = useState(false);
	const [priceInput, setPriceInput] = useState("");
	const [defaultPrice, setDefaultPrice] = useState(389);
	const [hasCustomPrice, setHasCustomPrice] = useState(false);
	const [priceSaving, setPriceSaving] = useState(false);
	const [savedPrice, setSavedPrice] = useState(""); // last persisted value (string)
	const [justSaved, setJustSaved] = useState(false); // brief confirmation flash
	const savedTimerRef = useRef(null);
	// Billing start date: when set, billing begins from this date (no trial).
	// Empty string => use the default (user createdAt + 15-day trial).
	const [startDateInput, setStartDateInput] = useState(""); // yyyy-mm-dd for <input type=date>
	const [savedStartDate, setSavedStartDate] = useState(""); // last persisted (yyyy-mm-dd)
	const [hasCustomStart, setHasCustomStart] = useState(false); // explicit override set?
	const [startDateSaving, setStartDateSaving] = useState(false);
	const [startDateJustSaved, setStartDateJustSaved] = useState(false);
	const startSavedTimerRef = useRef(null);
	// eslint-disable-next-line no-unused-vars
	const { workspaceId, workspaceName } = useParams();
	// eslint-disable-next-line no-unused-vars
	const getRowClass = (user) => {
		if (user.notOnboarded) {
			return "not-onboarded-row";
		}
		return "";
	};
	// eslint-disable-next-line no-unused-vars
	const navigate = useNavigate();

	useEffect(() => {
		fetchWorkspaceUsers();
		fetchBillingStatus();
		// Poll every 60s so online/offline status stays fresh without manual page reload.
		const interval = setInterval(fetchWorkspaceUsers, 60000);
		return () => clearInterval(interval);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workspaceId]);

	useEffect(() => {
		return () => {
			if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
			if (startSavedTimerRef.current) clearTimeout(startSavedTimerRef.current);
		};
	}, []);

	const fetchBillingStatus = async () => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/billing-status`,
			);
			if (!response.ok) return;
			const data = await response.json();
			setBillingOptedOut(Boolean(data.optedOut));
			const fallback = data.defaultPricePerUser ?? 389;
			const current = String(data.pricePerUser != null ? data.pricePerUser : fallback);
			setDefaultPrice(fallback);
			setHasCustomPrice(data.pricePerUser != null);
			setPriceInput(current);
			setSavedPrice(current);
			// Billing start date -> yyyy-mm-dd for the date input. When no explicit
			// override is set, pre-fill with the default trial-end date so the field
			// always shows the date billing actually starts.
			const effectiveStart = data.billingStartDate || data.defaultBillingStartDate;
			const startStr = effectiveStart ? toDateInputValue(effectiveStart) : "";
			setHasCustomStart(data.billingStartDate != null);
			setStartDateInput(startStr);
			setSavedStartDate(startStr);
		} catch (err) {
			console.error("Error fetching billing status:", err);
		}
	};

	// Format an ISO/date string as the local yyyy-mm-dd expected by <input type=date>.
	const toDateInputValue = (dateLike) => {
		const d = new Date(dateLike);
		if (Number.isNaN(d.getTime())) return "";
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, "0");
		const dd = String(d.getDate()).padStart(2, "0");
		return `${yyyy}-${mm}-${dd}`;
	};

	const handleSaveStartDate = async () => {
		const trimmed = startDateInput.trim();
		// Empty input clears the override (revert to default createdAt + trial).
		const value = trimmed === "" ? null : trimmed;
		setStartDateSaving(true);
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/billing-start-date`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ billingStartDate: value }),
				},
			);
			if (!response.ok) throw new Error(`Failed: ${response.status}`);
			const data = await response.json();
			// After clearing, fall back to the default trial-end date so the field
			// keeps showing when billing actually starts.
			const effectiveStart = data.billingStartDate || data.defaultBillingStartDate;
			const startStr = effectiveStart ? toDateInputValue(effectiveStart) : "";
			setHasCustomStart(data.billingStartDate != null);
			setStartDateInput(startStr);
			setSavedStartDate(startStr);
			setStartDateJustSaved(true);
			if (startSavedTimerRef.current) clearTimeout(startSavedTimerRef.current);
			startSavedTimerRef.current = setTimeout(() => setStartDateJustSaved(false), 2000);
		} catch (err) {
			console.error("Error saving billing start date:", err);
			alert("Could not save the billing start date. Please try again.");
		} finally {
			setStartDateSaving(false);
		}
	};

	const handleSavePrice = async () => {
		const trimmed = priceInput.trim();
		// Empty input resets to the default rate (clears the override).
		const value = trimmed === "" ? null : Number(trimmed);
		if (value !== null && (!Number.isFinite(value) || value <= 0)) {
			alert("Please enter a positive amount, or clear the field to use the default.");
			return;
		}
		setPriceSaving(true);
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/billing-price`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ pricePerUser: value }),
				},
			);
			if (!response.ok) throw new Error(`Failed: ${response.status}`);
			const data = await response.json();
			const fallback = data.defaultPricePerUser ?? defaultPrice;
			const current = String(data.pricePerUser != null ? data.pricePerUser : fallback);
			setHasCustomPrice(data.pricePerUser != null);
			setPriceInput(current);
			setSavedPrice(current);
			// Flash a brief "Saved ✓" confirmation, then return to just the input.
			setJustSaved(true);
			if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
			savedTimerRef.current = setTimeout(() => setJustSaved(false), 2000);
		} catch (err) {
			console.error("Error saving billing price:", err);
			alert("Could not save the amount. Please try again.");
		} finally {
			setPriceSaving(false);
		}
	};

	const handleToggleBilling = async () => {
		const next = !billingOptedOut;
		setBillingUpdating(true);
		setBillingOptedOut(next); // optimistic
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/billing-optout`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ optedOut: next }),
				},
			);
			if (!response.ok) throw new Error(`Failed: ${response.status}`);
			const data = await response.json();
			setBillingOptedOut(Boolean(data.optedOut));
		} catch (err) {
			console.error("Error updating billing opt-out:", err);
			setBillingOptedOut(!next); // revert on failure
		} finally {
			setBillingUpdating(false);
		}
	};

	const fetchWorkspaceUsers = async () => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/users`,
			);
			if (!response.ok) {
				throw new Error(
					`Failed to fetch workspace users: ${response.status}`,
				);
			}
			const data = await response.json();
			setUsers(data.users);
			setLoading(false);
		} catch (err) {
			console.error("Error fetching users:", err); // Debug log
			setError(err.message);
			setLoading(false);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return "Never";
		const date = new Date(dateString);
		const now = new Date();
		const diffMs = now - date;
		const diffMins = Math.floor(diffMs / 60000);
		const diffHours = Math.floor(diffMs / 3600000);
		const diffDays = Math.floor(diffMs / 86400000);

		if (diffMins < 1) return "Just now";
		if (diffMins < 60) return `${diffMins}m ago`;
		if (diffHours < 24) return `${diffHours}h ago`;
		if (diffDays < 7) return `${diffDays}d ago`;
		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: diffDays > 365 ? "numeric" : undefined,
		});
	};

	const formatFullDate = (dateString) => {
		if (!dateString) return "";
		const date = new Date(dateString);
		return date.toLocaleString("en-US", {
			weekday: "short",
			month: "short",
			day: "numeric",
			year: "numeric",
			hour: "numeric",
			minute: "2-digit",
			second: "2-digit",
			hour12: true,
		});
	};
	useEffect(() => {
		fetchDailyInteractionData();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workspaceId, timeRange]);

	useEffect(() => {
		fetchDailyTranscriptData();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workspaceId, transcriptRange]);

	useEffect(() => {
		fetchDailyCopilotData();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workspaceId, copilotRange]);

	useEffect(() => {
		fetchHourlyActiveUsers();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workspaceId, timeInterval]);

	// Updated fetch function for workspace-specific daily interaction data
	const fetchDailyInteractionData = async () => {
		try {
			setLoadingEngagement(true);
			console.log(`🔍 Fetching daily interactions for workspace ${workspaceId} with timeRange: ${timeRange}`);
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/daily-interactions?timeRange=${timeRange}`,
			);
			if (!response.ok) {
				console.error(`❌ Failed to fetch daily interactions: ${response.status} ${response.statusText}`);
				console.warn("🔄 Falling back to mock data");
				// Fallback to mock data if API fails
				const mockData = generateMockDailyInteractionData();
				console.log(`📊 Mock data generated: ${mockData.length} days`, mockData);
				setDailyInteractionData(mockData);
			} else {
				const result = await response.json();
				console.log("✅ Daily interactions API response:", result);
				console.log(`📊 API returned ${result.data ? result.data.length : result.length} days of data`);
				setDailyInteractionData(result.data || result);
			}
		} catch (err) {
			console.error("❌ Error fetching daily interaction data:", err);
			console.error("🌐 API URL was:", `${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/daily-interactions?timeRange=${timeRange}`);
			console.warn("🔄 Falling back to mock data");
			// Generate mock data if API fails
			const mockData = generateMockDailyInteractionData();
			console.log(`📊 Mock data generated: ${mockData.length} days`, mockData);
			setDailyInteractionData(mockData);
		} finally {
			setLoadingEngagement(false);
		}
	};

	// Mock data generator for daily interactions (workspace-specific)
	const generateMockDailyInteractionData = () => {
		const data = [];
		const now = new Date();
		let startDate, endDate;

		// Calculate date range based on timeRange parameter
		switch (timeRange) {
			case "this-month":
				startDate = new Date(now.getFullYear(), now.getMonth(), 1);
				endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				break;
			case "last-month":
				startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
				endDate = new Date(now.getFullYear(), now.getMonth(), 0);
				break;
			case "all-time":
				// Mock 6 months of data for "all time"
				startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
				endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				break;
			default:
				// Default to this month
				startDate = new Date(now.getFullYear(), now.getMonth(), 1);
				endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		}

		// Generate data for each day in the range
		const currentDate = new Date(startDate);
		while (currentDate <= endDate) {
			// Generate realistic patterns with some randomness
			const baseValue = 20 + Math.random() * 10;
			const dayOfWeek = currentDate.getDay();
			const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
			const factor = isWeekend ? 0.6 : 1;

			data.push({
				date: currentDate.toLocaleDateString("en-US", {
					month: "short",
					day: "numeric",
				}),
				fullDate: new Date(currentDate),
				dms: Math.floor(
					baseValue * factor * (1.5 + Math.random() * 0.5),
				),
				knocks: Math.floor(
					baseValue * factor * (0.7 + Math.random() * 0.3),
				),
				invites: Math.floor(
					baseValue * factor * (0.5 + Math.random() * 0.3),
				),
			});

			// Move to next day
			currentDate.setDate(currentDate.getDate() + 1);
		}

		return data;
	};

	// Fetch workspace-specific daily transcript counts (mirrors interactions).
	const fetchDailyTranscriptData = async () => {
		try {
			setLoadingTranscripts(true);
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/daily-transcripts?timeRange=${transcriptRange}`,
			);
			if (!response.ok) {
				console.error(`❌ Failed to fetch daily transcripts: ${response.status} ${response.statusText}`);
				setTranscriptData(generateMockDailyTranscriptData());
			} else {
				const result = await response.json();
				setTranscriptData(result.data || result);
			}
		} catch (err) {
			console.error("❌ Error fetching daily transcript data:", err);
			setTranscriptData(generateMockDailyTranscriptData());
		} finally {
			setLoadingTranscripts(false);
		}
	};

	// Mock fallback for the transcripts chart, used ONLY if the API call fails.
	// Duration and words are hard, real quantities — we never invent them, so they
	// stay 0 in the fallback rather than showing fabricated minutes/words.
	const generateMockDailyTranscriptData = () => {
		return generateMockDailyInteractionData().map((row) => ({
			date: row.date,
			fullDate: row.fullDate,
			transcripts: Math.floor((row.knocks || 0) * 0.4),
			durationMinutes: 0,
			words: 0,
		}));
	};

	// Fetch workspace-specific daily copilot-chat counts (mirrors transcripts).
	const fetchDailyCopilotData = async () => {
		try {
			setLoadingCopilot(true);
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/daily-copilot-chats?timeRange=${copilotRange}`,
			);
			if (!response.ok) {
				console.error(`❌ Failed to fetch daily copilot chats: ${response.status} ${response.statusText}`);
				setCopilotData(generateMockDailyCopilotData());
			} else {
				const result = await response.json();
				setCopilotData(result.data || result);
			}
		} catch (err) {
			console.error("❌ Error fetching daily copilot chat data:", err);
			setCopilotData(generateMockDailyCopilotData());
		} finally {
			setLoadingCopilot(false);
		}
	};

	// Mock fallback for the copilot chart, used ONLY if the API call fails.
	const generateMockDailyCopilotData = () => {
		return generateMockDailyInteractionData().map((row) => ({
			date: row.date,
			fullDate: row.fullDate,
			copilotResponses: Math.floor((row.knocks || 0) * 0.5),
		}));
	};

	// Format a minute count as "Xh Ym" (or "Ym" under an hour) for display.
	const formatMinutes = (mins) => {
		const m = Math.round(mins || 0);
		if (m < 60) return `${m}m`;
		return `${Math.floor(m / 60)}h ${m % 60}m`;
	};

	// Fetch workspace-specific hourly active users data
	const fetchHourlyActiveUsers = async () => {
		try {
			setLoadingHourlyData(true);
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/hourly-active-users?interval=${timeInterval}`,
			);
			if (!response.ok) {
				console.error(`Failed to fetch hourly active users: ${response.status} ${response.statusText}`);
				// Fallback to mock data if API fails
				setHourlyData(generateMockHourlyData());
			} else {
				const data = await response.json();
				console.log("Hourly active users API response:", data);
				
				// Process the data to ensure it has the correct structure
				const processedData = data.map(item => ({
					...item,
					timestamp: item.timestamp || new Date(item.time).toISOString(),
					activeUsers: item.activeUsers || 0
				}));

				setHourlyData(processedData);
			}
		} catch (error) {
			console.error("Error fetching hourly active users:", error);
			console.error("API URL was:", `${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}/hourly-active-users?interval=${timeInterval}`);
			// Generate mock data if API fails
			setHourlyData(generateMockHourlyData());
		} finally {
			setLoadingHourlyData(false);
		}
	};

	// Generate mock hourly data for workspace
	const generateMockHourlyData = () => {
		const now = new Date();
		const data = [];

		// Determine the number of data points based on the selected interval
		const pointsPerDay = timeInterval === "5min" ? 288 : // 24 * 12
			timeInterval === "15min" ? 96 : // 24 * 4
			timeInterval === "30min" ? 48 : // 24 * 2
			24; // 1 hour intervals

		const minutesToSubtract = timeInterval === "5min" ? 5 :
			timeInterval === "15min" ? 15 :
			timeInterval === "30min" ? 30 :
			60; // 1 hour

		for (let i = pointsPerDay - 1; i >= 0; i--) {
			const timestamp = new Date(now);
			timestamp.setMinutes(timestamp.getMinutes() - i * minutesToSubtract);

			// Generate realistic pattern with higher values during work hours
			const hourOfDay = timestamp.getHours();
			let baseValue = 5; // Base number of users for this workspace

			// Increase during work hours (9am-6pm)
			if (hourOfDay >= 9 && hourOfDay <= 18) {
				baseValue = 15;
			}
			// Peak hours (11am-3pm)
			if (hourOfDay >= 11 && hourOfDay <= 15) {
				baseValue = 25;
			}
			// Lunch dip (12-1pm)
			if (hourOfDay === 12) {
				baseValue = 12;
			}

			// Add some randomness
			const activeUsers = Math.floor(
				baseValue + Math.random() * baseValue * 0.3,
			);

			data.push({
				hour: timestamp.toLocaleTimeString("en-US", {
					hour: "numeric",
					minute: "2-digit",
					hour12: true,
				}),
				timestamp: timestamp.toISOString(),
				activeUsers,
			});
		}

		return data;
	};

	// eslint-disable-next-line no-unused-vars
	const formatPercentage = (value) => {
		if (value === undefined || value === null) return "N/A";
		const formatted = value.toFixed(1);
		return `${formatted > 0 ? "+" : ""}${formatted}%`;
	};
	// Sorting function
	const handleSort = (key) => {
		let direction;
		if (sortConfig.key === key) {
			// Toggle direction
			direction = sortConfig.direction === "asc" ? "desc" : "asc";
		} else {
			// Default direction: descending for dates/numbers, ascending for strings
			direction = ["lastSeenAt", "isOnline", "totalKnocks", "totalInvites", "dmCount", "sessioncount", "timeSpentMinutes", "currentCabinUser", "avgSessionTimeWeek"].includes(key) ? "desc" : "asc";
		}
		setSortConfig({ key, direction });
	};

	// Filter users based on search term
	const getFilteredUsers = () => {
		if (!searchTerm.trim()) return users;
		
		return users.filter(user => 
			user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.userType.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.invitationStatus.toLowerCase().includes(searchTerm.toLowerCase())
		);
	};

	// Get sorted data
	const getSortedUsers = () => {
		const filteredUsers = getFilteredUsers();
		if (!sortConfig.key) return filteredUsers;

		return [...filteredUsers].sort((a, b) => {
			let aValue = a[sortConfig.key];
			let bValue = b[sortConfig.key];

			// Handle different data types
			if (sortConfig.key === "lastSeenAt") {
				// Online users are treated as "seen right now"
				// Users with no lastSeenAt ("Never") always go to the bottom
				const nowTs = Date.now();
				const aHasDate = a.isOnline || aValue;
				const bHasDate = b.isOnline || bValue;
				if (!aHasDate && !bHasDate) return 0;
				if (!aHasDate) return 1;  // a ("Never") goes to bottom
				if (!bHasDate) return -1; // b ("Never") goes to bottom
				aValue = a.isOnline ? nowTs : new Date(aValue).getTime();
				bValue = b.isOnline ? nowTs : new Date(bValue).getTime();
			} else if (typeof aValue === "string") {
				aValue = aValue.toLowerCase();
				bValue = bValue.toLowerCase();
			} else if (typeof aValue === "number" || !isNaN(aValue)) {
				aValue = Number(aValue);
				bValue = Number(bValue);
			}

			if (aValue < bValue) {
				return sortConfig.direction === "asc" ? -1 : 1;
			}
			if (aValue > bValue) {
				return sortConfig.direction === "asc" ? 1 : -1;
			}
			return 0;
		});
	};

	// Get sort indicator
	const getSortIndicator = (columnKey) => {
		if (sortConfig.key !== columnKey) {
			return "";
		}
		return sortConfig.direction === "asc" ? " ▲" : " ▼";
	};

	// Helper function to format numbers with commas for display
	const formatDetailedNumber = (num) => {
		return new Intl.NumberFormat().format(num);
	};

	// Hourly Active Users Chart Component (adapted from WorkspaceAnalytics.js)
	const HourlyActiveUsersChart = ({
		data,
		height = 300,
		color = "#4299e1",
	}) => {
		const [hoveredPoint, setHoveredPoint] = useState(null);
		const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

		if (!data || data.length === 0) return <div>No data available</div>;

		// Ensure all data items have required properties and filter out invalid ones
		const validData = data.filter((item) => 
			item && 
			typeof item.activeUsers === 'number' && 
			item.timestamp
		);

		if (validData.length === 0) {
			return <div>No valid data available for the selected time interval</div>;
		}

		const maxValue = Math.max(...validData.map((d) => d.activeUsers));
		const minValue = 0; // Start from 0 for better visualization
		const range = maxValue - minValue || 1;

		// Create better y-axis labels with nice round numbers
		const createYAxisLabels = (min, max) => {
			const range = max - min;
			const step = Math.ceil(range / 4); // Create 4-5 labels
			const roundedMin = Math.floor(min / step) * step;
			const roundedMax = Math.ceil(max / step) * step;

			const labels = [];
			for (let i = roundedMax; i >= roundedMin; i -= step) {
				if (i >= min && i <= max) {
					labels.push(i);
				}
			}

			// Ensure we have at least min and max
			if (!labels.includes(max)) labels.unshift(max);
			if (!labels.includes(min)) labels.push(min);

			return labels.sort((a, b) => b - a); // Sort descending for top to bottom
		};

		const yLabels = createYAxisLabels(minValue, maxValue);

		// Create smooth curve using cubic bezier
		const createSmoothPath = (data) => {
			let path = "";
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y =
					100 - ((data[i].activeUsers - minValue) / range) * 100;

				if (i === 0) {
					path += `M ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 -
						((data[i - 1].activeUsers - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}
			return path;
		};

		const pathData = createSmoothPath(validData);

		// Create filled area path
		const createFillPath = (data) => {
			let path = "";
			// Start from bottom left
			path += `M 0 100`;

			// Add the smooth curve
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y =
					100 - ((data[i].activeUsers - minValue) / range) * 100;

				if (i === 0) {
					path += ` L ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 -
						((data[i - 1].activeUsers - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}

			// Close the path by going to bottom right and back to start
			path += ` L 100 100 Z`;
			return path;
		};

		const fillPath = createFillPath(validData);

		// Handle mouse movement over the chart
		const handleMouseMove = (event) => {
			const rect = event.currentTarget.getBoundingClientRect();
			const x = ((event.clientX - rect.left) / rect.width) * 100;

			// Find the closest data point
			const dataIndex = Math.round((x / 100) * (validData.length - 1));
			const clampedIndex = Math.max(
				0,
				Math.min(validData.length - 1, dataIndex),
			);

			setHoveredPoint(clampedIndex);
			setMousePosition({
				x: event.clientX - rect.left,
				y: event.clientY - rect.top,
			});
		};

		const handleMouseLeave = () => {
			setHoveredPoint(null);
		};

		return (
			<div className="relative">
				<div
					className="chart-container hourly-chart"
					style={{ height: height + 40, position: "relative" }}
				>
					<svg
						width="100%"
						height={height}
						viewBox="0 0 100 100"
						preserveAspectRatio="none"
						style={{ overflow: "hidden" }}
						onMouseMove={handleMouseMove}
						onMouseLeave={handleMouseLeave}
					>
						{/* Filled area under the curve */}
						<path
							d={fillPath}
							fill={color}
							fillOpacity="0.02"
							stroke="none"
						/>

						{/* Main curve line */}
						<path
							d={pathData}
							fill="none"
							stroke={color}
							strokeWidth="2"
							vectorEffect="non-scaling-stroke"
						/>

						{/* Hover indicator lines */}
						{hoveredPoint !== null && (
							<g>
								{/* Vertical line */}
								<line
									x1={
										(hoveredPoint / (validData.length - 1)) * 100
									}
									y1="0"
									x2={
										(hoveredPoint / (validData.length - 1)) * 100
									}
									y2="100"
									stroke={color}
									strokeWidth="1"
									strokeDasharray="3,3"
									vectorEffect="non-scaling-stroke"
									opacity="0.7"
								/>
								{/* Horizontal line */}
								<line
									x1="0"
									y1={
										100 -
										((validData[hoveredPoint].activeUsers -
											minValue) /
											range) *
											100
									}
									x2="100"
									y2={
										100 -
										((validData[hoveredPoint].activeUsers -
											minValue) /
											range) *
											100
									}
									stroke={color}
									strokeWidth="1"
									strokeDasharray="3,3"
									vectorEffect="non-scaling-stroke"
									opacity="0.7"
								/>
							</g>
						)}
					</svg>
					<div className="chart-labels">
						<div className="y-axis-labels hourly-labels">
							{yLabels.map((value, index) => (
								<span
									key={value}
									style={{
										position: "absolute",
										top: `${(index / (yLabels.length - 1)) * 100}%`,
										transform: "translateY(-50%)",
									}}
								>
									{formatDetailedNumber(value)}
								</span>
							))}
						</div>
					</div>
					{/* X-axis hour labels */}
					<div className="x-axis-labels">
						{(() => {
							const hourlyLabels = validData
								.map((item, i) => ({ item, originalIndex: i }))
								.filter(({ item, originalIndex: i }) => {
									if (!item || !item.timestamp) return false;
									if (timeInterval === "5min")
										return i % 12 === 0;
									if (timeInterval === "15min")
										return i % 4 === 0;
									if (timeInterval === "30min")
										return i % 2 === 0;
									if (timeInterval === "1hour") return true;
									return false;
								});

							// On narrow screens, thin labels further to ~6 max
							const isMobile = window.innerWidth < 768;
							const maxLabels = isMobile ? 6 : hourlyLabels.length;
							const step = maxLabels < hourlyLabels.length
								? Math.ceil(hourlyLabels.length / maxLabels)
								: 1;

							return hourlyLabels
								.filter((_, i) => i % step === 0);
						})()
							.map(({ item, originalIndex }, index) => {
								if (!item || !item.timestamp) return null;

								const x =
									(originalIndex / (validData.length - 1)) *
									100;

								let timeLabel;
								try {
									const isMobile = window.innerWidth < 768;
									timeLabel = new Date(
										item.timestamp,
									).toLocaleTimeString("en-US", {
										hour: "numeric",
										minute: isMobile ? undefined : (
											timeInterval === "1hour"
												? undefined
												: "2-digit"
										),
										hour12: true,
									});
								} catch (error) {
									console.error(
										"Error formatting time:",
										error,
									);
									timeLabel = "Invalid time";
								}

								return (
									<span
										key={`${item.timestamp}-${index}`}
										className="x-label"
										style={{
											position: "absolute",
											left: `${x}%`,
											transform:
												"translateX(-50%) rotate(-45deg)",
											transformOrigin: "top center",
											fontSize: window.innerWidth < 768 ? "0.6rem" : "0.75rem",
											color: "var(--text-secondary)",
											whiteSpace: "nowrap",
											marginTop: "8px",
										}}
									>
										{timeLabel}
									</span>
								);
							})}
					</div>

					{/* Tooltip */}
					{hoveredPoint !== null && (
						<div
							className="chart-tooltip"
							style={{
								position: "absolute",
								left: `${mousePosition.x + 10}px`,
								top: `${mousePosition.y - 10}px`,
								background: "rgba(0, 0, 0, 0.8)",
								color: "white",
								padding: "8px 12px",
								borderRadius: "6px",
								fontSize: "12px",
								pointerEvents: "none",
								zIndex: 1000,
								whiteSpace: "nowrap",
								boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
							}}
						>
							<div style={{ fontWeight: "bold" }}>
								{new Date(
									validData[hoveredPoint].timestamp,
								).toLocaleTimeString("en-US", {
									hour: "numeric",
									minute: "2-digit",
									hour12: true,
								})}
							</div>
							<div>
								{formatDetailedNumber(
									validData[hoveredPoint].activeUsers,
								)}{" "}
								Active Users
							</div>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Daily Interaction Chart Component (adapted from WorkspaceAnalytics.js)
	const DailyInteractionChart = ({
		data,
		height = 400,
		// Parameterized so this same chart renders both the interactions view
		// (DMs / Knocks / Invites) and the transcripts view (single line).
		// Defaults preserve the original three-series behavior.
		metrics = [
			{ key: "dms", label: "DMs", color: "#4299E1" },
			{ key: "knocks", label: "Knocks", color: "#48BB78" },
			{ key: "invites", label: "Invites", color: "#ED8936" },
		],
		processRow = (item) => ({
			...item,
			dms: item.dms || 0,
			knocks: item.knocks || 0,
			invites: item.invites || 0,
		}),
		// Optional: (hoveredData) => [{ label, value }] extra read-only rows shown
		// in the hover tooltip (e.g. per-day duration/words that aren't plotted).
		extraTooltipRows = null,
	}) => {
		const [hoveredData, setHoveredData] = useState(null);
		const [forceUpdate, setForceUpdate] = useState(0);
		const chartRef = useRef(null);

		// Force re-render when container is mounted and has dimensions
		useEffect(() => {
			if (chartRef.current && chartRef.current.clientWidth > 0) {
				// Small delay to ensure layout is complete, then force update once
				const timeout = setTimeout(() => {
					setForceUpdate(1);
				}, 100);
				
				return () => clearTimeout(timeout);
			}
		}, [data]);

		// Add resize observer to handle window resize
		useEffect(() => {
			if (!chartRef.current) return;

			const resizeObserver = new ResizeObserver(() => {
				// Only update if we have a valid container width
				if (chartRef.current && chartRef.current.clientWidth > 0) {
					setTimeout(() => {
						setForceUpdate(prev => prev + 1);
					}, 100);
				}
			});

			resizeObserver.observe(chartRef.current);

			return () => {
				resizeObserver.disconnect();
			};
		}, []);

		// Process each row via the (parameterized) processRow function.
		const processedData = useMemo(() => {
			if (!data || data.length === 0) return [];
			return data.map(processRow);
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [data]);

		// Find max value across all metrics for y-axis scale
		const maxValue = useMemo(() => {
			if (!processedData || processedData.length === 0) return 100;
			const raw = Math.max(
				...processedData.map((item) =>
					Math.max(...metrics.map((m) => item[m.key] || 0)),
				),
			);
			return (raw > 0 ? raw : 100) * 1.1; // Add 10% padding to the max value
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [processedData]);

		const createYAxisLabels = (min, max) => {
			const step = Math.ceil(max / 5);
			const labels = [];
			for (let i = 0; i <= 5; i++) {
				labels.push(i * step);
			}
			return labels;
		};

		const yLabels = createYAxisLabels(0, maxValue);

		// Modify the path creation functions to ensure points near the edge are visible
		const createSmoothPath = (data, metricKey) => {
			if (!data || data.length === 0 || !chartRef.current) return "";

			const chartWidth = chartRef.current.clientWidth - 60; // Account for y-axis width
			const chartHeight = height - 60; // Account for x-axis height

			// Adjust the xStep to ensure the last points are visible within the chart boundary
			const xStep = (chartWidth - 80) / (data.length - 1); // Further increase padding to keep points inside
			const yScale = chartHeight / maxValue;

			let path = `M 30,${chartHeight - (data[0][metricKey] || 0) * yScale}`;

			for (let i = 1; i < data.length; i++) {
				const x = 30 + i * xStep;
				const y = chartHeight - (data[i][metricKey] || 0) * yScale;
				const prevX = 30 + (i - 1) * xStep;
				const prevY =
					chartHeight - (data[i - 1][metricKey] || 0) * yScale;

				const cpx1 = prevX + xStep / 3;
				const cpy1 = prevY;
				const cpx2 = x - xStep / 3;
				const cpy2 = y;

				path += ` C ${cpx1},${cpy1} ${cpx2},${cpy2} ${x},${y}`;
			}

			return path;
		};

		const createFillPath = (data, metricKey) => {
			if (!data || data.length === 0 || !chartRef.current) return "";

			const chartWidth = chartRef.current.clientWidth - 60;
			const chartHeight = height - 60;

			// Adjust the xStep to ensure the last points are visible within the chart boundary
			const xStep = (chartWidth - 80) / (data.length - 1); // Further increase padding to keep points inside
			const yScale = chartHeight / maxValue;

			let path = `M 30,${chartHeight - (data[0][metricKey] || 0) * yScale}`;

			for (let i = 1; i < data.length; i++) {
				const x = 30 + i * xStep;
				const y = chartHeight - (data[i][metricKey] || 0) * yScale;
				const prevX = 30 + (i - 1) * xStep;
				const prevY =
					chartHeight - (data[i - 1][metricKey] || 0) * yScale;

				const cpx1 = prevX + xStep / 3;
				const cpy1 = prevY;
				const cpx2 = x - xStep / 3;
				const cpy2 = y;

				path += ` C ${cpx1},${cpy1} ${cpx2},${cpy2} ${x},${y}`;
			}

			const lastX = 30 + (data.length - 1) * xStep; // Ensure the last X stays within bounds

			return `${path} L ${lastX},${chartHeight} L 30,${chartHeight} Z`;
		};

		const handleMouseMove = (event) => {
			if (!chartRef.current || !processedData || processedData.length === 0) return;

			const chartRect = chartRef.current.getBoundingClientRect();
			const chartWidth = chartRect.width - 60; // Account for y-axis
			const mouseX = event.clientX - chartRect.left - 30; // Adjust for y-axis offset

			// Adjust the xStep to match the path creation functions
			const xStep = (chartWidth - 80) / (processedData.length - 1);
			const index = Math.min(
				Math.max(Math.round(mouseX / xStep), 0),
				processedData.length - 1,
			);

			if (index >= 0 && index < processedData.length) {
				setHoveredData({
					...processedData[index],
					x: 30 + index * xStep, // Add y-axis offset
					index: index,
					mouseX: event.clientX - chartRect.left,
					mouseY: event.clientY - chartRect.top,
				});
			}
		};

		const handleMouseLeave = () => {
			setHoveredData(null);
		};

		// Calculate data point positions for all metrics - adjusted to match path creation
		const dataPoints = useMemo(() => {
			if (!processedData || processedData.length === 0 || !chartRef.current || chartRef.current.clientWidth <= 0) return [];

			const chartWidth = chartRef.current.clientWidth - 60;
			const chartHeight = height - 60;
			// Adjust the xStep to ensure the last points are visible within the chart boundary
			const xStep = (chartWidth - 80) / (processedData.length - 1);
			const yScale = chartHeight / maxValue;

			return processedData.map((item, index) => {
				const x = 30 + index * xStep;

				return {
					index,
					x,
					date: item.date,
					points: metrics.map((metric) => ({
						key: metric.key,
						color: metric.color,
						x,
						y: chartHeight - (item[metric.key] || 0) * yScale,
						value: item[metric.key] || 0,
					})),
				};
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [processedData, maxValue, metrics, height, forceUpdate]);

		// Completely revised getXAxisLabels function to show only key dates
		const getXAxisLabels = () => {
			if (!processedData || processedData.length === 0) return [];

			const totalPoints = processedData.length;

			// Fewer labels on mobile to prevent overlap
			const isMobile = window.innerWidth < 768;
			const middleLabelsCount = isMobile ? 1 : 3;

			// Always include first date
			const result = [{ date: processedData[0].date, index: 0 }];

			const step = Math.floor(
				(totalPoints - 2) / (middleLabelsCount + 1),
			);

			for (let i = 1; i <= middleLabelsCount; i++) {
				const index = i * step;
				if (index > 0 && index < totalPoints - 1) {
					result.push({
						date: processedData[index].date,
						index,
					});
				}
			}

			// Always include last date (today)
			result.push({
				date: processedData[totalPoints - 1].date,
				index: totalPoints - 1,
			});

			return result;
		};

		// Early return after all hooks have been called
		if (!processedData || processedData.length === 0) {
			return (
				<div
					className="chart-container daily-interaction-chart"
					style={{
						position: "relative",
						width: "100%",
						height: `${height + 40}px`,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						overflow: "hidden",
						paddingBottom: "20px",
					}}
				>
					<div className="loading">Loading chart data...</div>
				</div>
			);
		}

		// Show loading while container is not ready to prevent incorrect initial render
		if (!chartRef.current || chartRef.current.clientWidth <= 0) {
			return (
				<div 
					className="chart-container daily-interaction-chart"
					ref={chartRef}
					style={{
						position: "relative",
						width: "100%",
						height: `${height + 40}px`,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						overflow: "hidden",
						paddingBottom: "20px",
					}}
				>
					<div className="loading">Loading chart...</div>
				</div>
			);
		}

		const xAxisLabels = getXAxisLabels();

		return (
			<div
				className="chart-container daily-interaction-chart"
				ref={chartRef}
				style={{
					position: "relative",
					width: "100%",
					height: `${height + 70}px`,
					overflow: "visible",
					paddingBottom: "20px",
				}}
			>
				<svg
					width="100%"
					height={height}
					onMouseMove={handleMouseMove}
					onMouseLeave={handleMouseLeave}
					style={{ overflow: "visible" }}
				>
					{/* Grid lines */}
					{yLabels.map((label, i) => (
						<line
							key={`grid-${i}`}
							x1="30"
							y1={
								height - 60 - (label / maxValue) * (height - 60)
							}
							x2="100%"
							y2={
								height - 60 - (label / maxValue) * (height - 60)
							}
							stroke="#e2e8f0"
							strokeWidth="1"
							strokeOpacity="0.3"
						/>
					))}

					{/* Fill paths */}
					{metrics.map((metric) => (
						<path
							key={`fill-${metric.key}`}
							d={createFillPath(processedData, metric.key)}
							fill={metric.color}
							opacity="0.1"
						/>
					))}

					{/* Line paths */}
					{metrics.map((metric) => (
						<path
							key={`line-${metric.key}`}
							d={createSmoothPath(processedData, metric.key)}
							fill="none"
							stroke={metric.color}
							strokeWidth="2"
						/>
					))}

					{/* Data points with increased opacity for better visibility */}
					{dataPoints.map((dayData) =>
						dayData.points.map((point) => {
							// Highlight the last day's points
							const isLastDay = dayData.index === processedData.length - 1;
							return (
								<circle
									key={`point-${dayData.index}-${point.key}`}
									cx={point.x}
									cy={point.y}
									r={isLastDay ? "5" : "4"} // Make last day's points larger
									fill={point.color}
									opacity={isLastDay ? "1" : "0.8"} // Make last day's points fully opaque
									stroke="#fff"
									strokeWidth={isLastDay ? "2" : "1.5"} // Thicker stroke for last day's points
								/>
							);
						}),
					)}

					{/* Hover indicator */}
					{hoveredData && (
						<>
							<line
								x1={hoveredData.x}
								y1="0"
								x2={hoveredData.x}
								y2={height - 60}
								stroke="#718096"
								strokeWidth="1"
								strokeDasharray="4,4"
							/>
							{metrics.map((metric) => {
								const pointY =
									height -
									60 -
									(hoveredData[metric.key] || 0) *
										((height - 60) / maxValue);
								return (
									<circle
										key={`hover-point-${metric.key}`}
										cx={hoveredData.x}
										cy={pointY}
										r="6" // Increased hover point size
										fill={metric.color}
										stroke="#fff"
										strokeWidth="2"
									/>
								);
							})}
						</>
					)}
				</svg>

				{/* Y-axis labels */}
				<div
					className="y-axis-labels daily-interaction-labels"
					style={{
						position: "absolute",
						left: 0,
						top: 0,
						height: `${height - 60}px`,
						width: "30px",
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						color: "#718096",
						fontSize: "10px",
						paddingTop: "5px",
						paddingBottom: "5px",
					}}
				>
					{yLabels.map((label, i) => (
						<span
							key={`y-label-${i}`}
							style={{
								position: "absolute",
								left: 0,
								bottom: `${(label / maxValue) * 100}%`,
								transform: "translateY(50%)",
								width: "100%",
								textAlign: "right",
								paddingRight: "5px",
							}}
						>
							{label}
						</span>
					))}
				</div>

				{/* X-axis labels with improved positioning */}
				<div
					style={{
						position: "absolute",
						bottom: "25px",
						left: "30px",
						width: "calc(100% - 30px)",
						height: "40px",
						display: "flex",
						zIndex: 5,
					}}
				>
					{xAxisLabels.map((item) => {
						// Calculate position based on chart width and leave more space at edges
						const chartWidth =
							chartRef.current?.clientWidth - 60 || 800;
						const xStep = (chartWidth - 80) / (processedData.length - 1);
						const xPos = 30 + item.index * xStep;

						// Highlight today's date (last date)
						const isLastDate = item.index === processedData.length - 1;

						// Special positioning for the last date to ensure it's visible
						const labelStyle = isLastDate
							? {
									position: "absolute",
									right: "10px", // Fixed position from right edge
									bottom: "15px",
									transform: "none", // Don't use translateX to avoid clipping
									textAlign: "right",
								}
							: {
									position: "absolute",
									left: `${xPos}px`,
									bottom: "15px",
									transform: "translateX(-50%)",
									textAlign: "center",
								};

						return (
							<div
								key={`x-label-${item.index}`}
								style={labelStyle}
							>
								<div
									className="daily-chart-x-label"
									style={{
										transform: isLastDate
											? "none"
											: "rotate(-45deg)",
										transformOrigin: "top left",
										fontSize: isLastDate
											? (window.innerWidth < 768 ? "11px" : "12px")
											: (window.innerWidth < 768 ? "10px" : "11px"),
										fontWeight: isLastDate ? "700" : "500",
										whiteSpace: "nowrap",
										paddingTop: "5px",
										backgroundColor: isLastDate
											? "rgba(0,0,0,0.5)"
											: "transparent",
										padding: isLastDate ? "3px 6px" : "0",
										borderRadius: isLastDate ? "4px" : "0",
									}}
								>
									{item.date}
								</div>
							</div>
						);
					})}
				</div>

				{/* Tooltip with improved positioning */}
				{hoveredData && (
					<div
						className="chart-tooltip"
						style={{
							position: "absolute",
							left: `${hoveredData.x}px`,
							top: Math.max(10, hoveredData.mouseY - 160), // Keep tooltip within chart top boundary
							padding: "10px 14px", // Larger padding
							borderRadius: "8px", // Rounder corners
							fontSize: "12px",
							zIndex: 1000,
							maxWidth: "180px", // Wider tooltip
							transform: "translateX(-50%)", // Center horizontally over the point
							pointerEvents: "none", // Ensure it doesn't interfere with mouse events
						}}
					>
						<div
							className="chart-tooltip-date"
							style={{
								fontWeight: "bold",
								marginBottom: "8px",
								fontSize: "14px", // Larger date text
								paddingBottom: "4px",
							}}
						>
							{hoveredData.date}
						</div>
						{metrics.map((metric) => (
							<div
								key={`tooltip-${metric.key}`}
								style={{
									marginTop: "6px",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<div
									style={{
										display: "flex",
										alignItems: "center",
									}}
								>
									<span
										style={{
											color: metric.color,
											marginRight: "5px",
											fontSize: "16px", // Larger bullet point
										}}
									>
										●
									</span>
									<span>{metric.label}:</span>
								</div>
								<strong style={{ marginLeft: "10px" }}>
									{hoveredData[metric.key] || 0}
								</strong>
							</div>
						))}
						{typeof extraTooltipRows === "function" &&
							extraTooltipRows(hoveredData).map((row) => (
								<div
									key={`tooltip-extra-${row.label}`}
									style={{
										marginTop: "6px",
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										opacity: 0.9,
									}}
								>
									<span>{row.label}:</span>
									<strong style={{ marginLeft: "10px" }}>
										{row.value}
									</strong>
								</div>
							))}
						<div
							style={{
								position: "absolute",
								bottom: "-8px",
								left: "50%",
								transform: "translateX(-50%)",
								width: 0,
								height: 0,
								borderLeft: "8px solid transparent",
								borderRight: "8px solid transparent",
								borderTop: "8px solid rgba(0, 0, 0, 0.85)",
							}}
						></div>
					</div>
				)}

				{/* Chart legend */}
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						gap: "20px",
						position: "absolute",
						bottom: "0",
						width: "100%",
						textAlign: "center",
					}}
				>
					{metrics.map((metric) => (
						<div
							key={`legend-${metric.key}`}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "5px",
							}}
						>
							<div
								style={{
									width: "12px",
									height: "12px",
									borderRadius: "2px",
									backgroundColor: metric.color,
								}}
							></div>
							<span>{metric.label}</span>
						</div>
					))}
				</div>
			</div>
		);
	};

	const handleDeleteWorkspace = async () => {
		if (
			window.confirm(
				"Are you sure you want to delete this workspace? This action cannot be undone.",
			)
		) {
			try {
				const response = await fetch(
					`${process.env.REACT_APP_BASE_URL}/workspaces/delete-workspace`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ workspaceId }),
					},
				);
				const data = await response.json();
				if (!response.ok || !data.success) {
					throw new Error(data.error || data.message || "Failed to delete workspace");
				}
				alert("Workspace deleted successfully!");
				navigate("/workspaces");
			} catch (error) {
				console.error("Error deleting workspace:", error);
				alert(`Failed to delete workspace: ${error.message}`);
			}
		}
	};

	if (loading) return <div className="loading">Loading user data...</div>;
	if (error) return <div className="error">Error: {error}</div>;

	return (
		<div className="dashboard-container">
			{/* Page Header */}
			<div className="workspace-page-header">
				<div className="workspace-page-title">
					<button className="back-btn" onClick={() => navigate("/workspaces")} title="Back to workspaces">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
							<path d="M19 12H5"></path>
							<path d="M12 19l-7-7 7-7"></path>
						</svg>
					</button>
					<h1>{decodeURIComponent(workspaceName)}</h1>
				</div>
				<div className="workspace-page-actions">
					<div
						className={`billing-price ${hasCustomPrice ? "is-custom" : ""}`}
						title={
							hasCustomPrice
								? "This workspace uses a custom price per user / month."
								: `Using the default price (₹${defaultPrice}). Set a custom amount and Save.`
						}
					>
						<span className="billing-price-label">₹ / user / mo</span>
						<input
							type="number"
							min="1"
							step="1"
							className="billing-price-input"
							value={priceInput}
							onChange={(e) => setPriceInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSavePrice();
							}}
							placeholder={String(defaultPrice)}
						/>
						{(() => {
							const dirty = priceInput.trim() !== savedPrice;
							if (!dirty && !priceSaving && !justSaved) return null;
							const showSaved = justSaved && !dirty;
							return (
								<button
									type="button"
									className={`billing-price-save ${showSaved ? "is-saved" : ""}`}
									onClick={handleSavePrice}
									disabled={priceSaving || !dirty}
								>
									{priceSaving ? (
										<span className="billing-price-spinner" aria-label="Saving" />
									) : showSaved ? (
										"Saved ✓"
									) : (
										"Save"
									)}
								</button>
							);
						})()}
					</div>
					<div
						className={`billing-price billing-start ${hasCustomStart ? "is-custom" : ""}`}
						title={
							hasCustomStart
								? "Custom billing start date. Billing is counted from this date; clear it to use the default (creation + 15-day trial)."
								: "Default billing start (workspace creation + 15-day free trial). Change the date to override."
						}
					>
						<span className="billing-price-label">Billing since</span>
						<input
							type="date"
							className="billing-price-input billing-start-input"
							value={startDateInput}
							onChange={(e) => setStartDateInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSaveStartDate();
							}}
						/>
						{(() => {
							const dirty = startDateInput.trim() !== savedStartDate;
							if (!dirty && !startDateSaving && !startDateJustSaved) return null;
							const showSaved = startDateJustSaved && !dirty;
							return (
								<button
									type="button"
									className={`billing-price-save ${showSaved ? "is-saved" : ""}`}
									onClick={handleSaveStartDate}
									disabled={startDateSaving || !dirty}
								>
									{startDateSaving ? (
										<span className="billing-price-spinner" aria-label="Saving" />
									) : showSaved ? (
										"Saved ✓"
									) : (
										"Save"
									)}
								</button>
							);
						})()}
					</div>
					<button
						type="button"
						className={`billing-toggle ${billingOptedOut ? "is-opted-out" : ""}`}
						onClick={handleToggleBilling}
						disabled={billingUpdating}
						title={
							billingOptedOut
								? "This workspace is excluded from revenue. Click to include it."
								: "This workspace is included in revenue. Click to exclude it."
						}
					>
						<span className="billing-toggle-switch" aria-hidden="true">
							<span className="billing-toggle-knob" />
						</span>
						<span className="billing-toggle-label">
							{billingOptedOut ? "Excluded from billing" : "Included in billing"}
						</span>
					</button>
					<button className="delete-workspace-btn" onClick={handleDeleteWorkspace}>
						Delete Workspace
					</button>
				</div>
			</div>

			{/* Search Section */}
			<div className="search-section">
				<div className="search-container">
					<div className="search-input-wrapper">
						<svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<circle cx="11" cy="11" r="8"></circle>
							<path d="m21 21-4.35-4.35"></path>
						</svg>
						<input
							type="text"
							placeholder="Search users by name, email, role, or status..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="search-input"
						/>
						{searchTerm && (
							<button
								onClick={() => setSearchTerm("")}
								className="clear-search-btn"
								title="Clear search"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<line x1="18" y1="6" x2="6" y2="18"></line>
									<line x1="6" y1="6" x2="18" y2="18"></line>
								</svg>
							</button>
						)}
					</div>
				</div>
				
				{/* Search Results Info */}
				{searchTerm && (
					<div className="search-results-info">
						{getSortedUsers().length === 0 ? (
							<span>No users found matching "{searchTerm}"</span>
						) : getSortedUsers().length === 1 ? (
							<span>Found 1 user matching "{searchTerm}"</span>
						) : (
							<span>Found {getSortedUsers().length} users matching "{searchTerm}"</span>
						)}
					</div>
				)}
			</div>

			{/* Desktop Table View */}
			<div className="table-container">
				<table className="workspace-table">
					<thead>
						<tr>
							<th
								onClick={() => handleSort("userName")}
								style={{ cursor: "pointer" }}
							>
								User Name{getSortIndicator("userName")}
							</th>
							<th
								onClick={() => handleSort("userEmail")}
								style={{ cursor: "pointer" }}
							>
								Email{getSortIndicator("userEmail")}
							</th>
							<th
								onClick={() => handleSort("userType")}
								style={{ cursor: "pointer" }}
							>
								Role{getSortIndicator("userType")}
							</th>
							<th
								onClick={() => handleSort("invitationStatus")}
								style={{ cursor: "pointer" }}
							>
								Invitation Status
								{getSortIndicator("invitationStatus")}
							</th>
							<th
								onClick={() => handleSort("totalKnocks")}
								style={{ cursor: "pointer" }}
							>
								Total Knocks{getSortIndicator("totalKnocks")}
							</th>
							<th
								onClick={() => handleSort("totalInvites")}
								style={{ cursor: "pointer" }}
							>
								Total Invites{getSortIndicator("totalInvites")}
							</th>
							<th
								onClick={() => handleSort("isOnline")}
								style={{ cursor: "pointer" }}
							>
								Status{getSortIndicator("isOnline")}
							</th>
							<th
								onClick={() => handleSort("currentPlatform")}
								style={{ cursor: "pointer" }}
							>
								Active On{getSortIndicator("currentPlatform")}
							</th>
							<th>
								Platforms
							</th>
							<th
								onClick={() => handleSort("appVersion")}
								style={{ cursor: "pointer" }}
							>
								App Version{getSortIndicator("appVersion")}
							</th>
							<th
								onClick={() => handleSort("lastSeenAt")}
								style={{ cursor: "pointer" }}
							>
								Last Seen{getSortIndicator("lastSeenAt")}
							</th>
							<th
								onClick={() => handleSort("dmCount")}
								style={{ cursor: "pointer" }}
							>
								DM Count{getSortIndicator("dmCount")}
							</th>
							<th
								onClick={() => handleSort("currentCabinUser")}
								style={{ cursor: "pointer" }}
							>
								Current Cabin User
								{getSortIndicator("currentCabinUser")}
							</th>
							<th
								onClick={() => handleSort("cabinAssigned")}
								style={{ cursor: "pointer" }}
							>
								Cabin Assigned
								{getSortIndicator("cabinAssigned")}
							</th>
							{/* <th
								onClick={() => handleSort("dmPercentageChange")}
								style={{ cursor: "pointer" }}
							>
								DM Change
								{getSortIndicator("dmPercentageChange")}
							</th> */}
							{/* <th
								onClick={() =>
									handleSort("knockPercentageChange")
								}
								style={{ cursor: "pointer" }}
							>
								Knock Change
								{getSortIndicator("knockPercentageChange")}
							</th>
							<th
								onClick={() =>
									handleSort("invitePercentageChange")
								}
								style={{ cursor: "pointer" }}
							>
								Invite Change
								{getSortIndicator("invitePercentageChange")}
							</th> */}
							<th
								onClick={() => handleSort("sessioncount")}
								style={{ cursor: "pointer" }}
							>
								sessionCount
								{getSortIndicator("sessioncount")}
							</th>
							<th
								onClick={() => handleSort("timeSpentMinutes")}
								style={{ cursor: "pointer" }}
							>
								Time Spent (mins)
								{getSortIndicator("timeSpentMinutes")}
							</th>
							<th
								onClick={() => handleSort("d7Retention")}
								style={{ cursor: "pointer" }}
								title="Percentage of days active in the last 7 days"
							>
								7-Day Activity Rate
								{getSortIndicator("d7Retention")}
								<span className="info-icon">ⓘ</span>
							</th>
							<th
								onClick={() => handleSort("avgSessionTimeWeek")}
								style={{ cursor: "pointer" }}
							>
								Avg Session (mins/week)
								{getSortIndicator("avgSessionTimeWeek")}
							</th>
						</tr>
					</thead>

					<tbody>
						{getSortedUsers().map((user, index) => (
							<tr key={index}>
								<td className="user-name">{user.userName}</td>
								<td className="user-email">{user.userEmail}</td>
								<td
									className={`user-role ${user.userType.toLowerCase()}`}
								>
									{user.userType}
								</td>
								<td
									className={`invitation-status ${user.invitationStatus.toLowerCase().replace(" ", "-")}`}
								>
									{user.invitationStatus}
								</td>
								<td className="metric-cell">
									{user.totalKnocks}
								</td>
								<td className="metric-cell">
									{user.totalInvites}
								</td>
								<td className="status-cell">
									<div
										className={`status-indicator ${user.isOnline ? "online" : "offline"}`}
									>
										<span className="status-dot"></span>
										{user.isOnline ? "Online" : "Offline"}
									</div>
								</td>
								<td className="metric-cell">
									{user.isOnline && user.currentPlatform
										? user.currentPlatform === "macos" ? "macOS"
											: user.currentPlatform.charAt(0).toUpperCase() + user.currentPlatform.slice(1)
										: "-"}
								</td>
								<td className="metric-cell">
									{user.platformsUsed && user.platformsUsed.length > 0
										? user.platformsUsed.map(p =>
											p === "macos" ? "macOS" : p.charAt(0).toUpperCase() + p.slice(1)
										).join(", ")
										: "-"}
								</td>
								<td className="metric-cell">
									{user.currentPlatform === "web"
										? "N/A"
										: user.appVersion || "-"}
								</td>
							<td
								className="last-seen-cell"
								title={user.isOnline ? "Currently online" : formatFullDate(user.lastSeenAt)}
							>
								{!user.isOnline &&
									formatDate(user.lastSeenAt)}
							</td>
								<td className="metric-cell">{user.dmCount}</td>
								<td className="metric-cell">
									{user.currentCabinUser}
								</td>
								<td className={`metric-cell cabin-assigned ${
									user.cabinAssigned === "Yes" ? "cabin-yes" : 
									user.cabinAssigned === "No" ? "cabin-no" : 
									"cabin-na"
								}`}>
									{user.cabinAssigned}
								</td>
								{/* <td
									className={`metric-cell ${user.dmPercentageChange > 0 ? "positive" : user.dmPercentageChange < 0 ? "negative" : ""}`}
								>
									{formatPercentage(user.dmPercentageChange)}
								</td> */}
								{/* <td
									className={`metric-cell ${user.knockPercentageChange > 0 ? "positive" : user.knockPercentageChange < 0 ? "negative" : ""}`}
								>
									{formatPercentage(
										user.knockPercentageChange,
									)}
								</td>
								<td
									className={`metric-cell ${user.invitePercentageChange > 0 ? "positive" : user.invitePercentageChange < 0 ? "negative" : ""}`}
								>
									{formatPercentage(
										user.invitePercentageChange,
									)}
								</td> */}
								<td className="metric-cell">
									{user.sessionCount}
								</td>
								<td className="metric-cell">
									{user.timeSpentMinutes}
								</td>
								<td
									className={`metric-cell ${
										parseFloat(user.d7Retention) >= 71.4
											? "high"
											: parseFloat(user.d7Retention) >=
												  42.8
												? "medium"
												: parseFloat(user.d7Retention) >
													  0
													? "low"
													: "inactive"
									}`}
								>
									{user.d7Retention}
									<span className="days-active">
										({user.activeDaysLastWeek || 0}/7 days)
									</span>
								</td>
								<td className="metric-cell">
									{user.avgSessionTimeWeek}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Mobile Card View */}
			<div className="mobile-user-cards">
				{getSortedUsers().map((user, index) => (
					<div className="mobile-user-card" key={index}>
						<div className="mobile-card-header">
							<div className="mobile-card-identity">
								<div className="mobile-card-name-row">
									<span className="mobile-card-name">{user.userName}</span>
									<div className={`status-indicator ${user.isOnline ? "online" : "offline"}`}>
										<span className="status-dot"></span>
										{user.isOnline ? "Online" : "Offline"}
									</div>
								</div>
								<span className="mobile-card-email">{user.userEmail}</span>
							</div>
							<div className="mobile-card-badges">
								<span className={`user-role ${user.userType.toLowerCase()}`}>{user.userType}</span>
								<span className={`invitation-status ${user.invitationStatus.toLowerCase().replace(" ", "-")}`}>
									{user.invitationStatus}
								</span>
							</div>
						</div>
						<div className="mobile-card-metrics">
							<div className="mobile-metric">
								<span className="mobile-metric-label">Knocks</span>
								<span className="mobile-metric-value">{user.totalKnocks}</span>
							</div>
							<div className="mobile-metric">
								<span className="mobile-metric-label">Invites</span>
								<span className="mobile-metric-value">{user.totalInvites}</span>
							</div>
							<div className="mobile-metric">
								<span className="mobile-metric-label">DMs</span>
								<span className="mobile-metric-value">{user.dmCount}</span>
							</div>
							<div className="mobile-metric">
								<span className="mobile-metric-label">Sessions</span>
								<span className="mobile-metric-value">{user.sessionCount}</span>
							</div>
							<div className="mobile-metric">
								<span className="mobile-metric-label">Time (mins)</span>
								<span className="mobile-metric-value">{user.timeSpentMinutes}</span>
							</div>
							<div className="mobile-metric">
								<span className="mobile-metric-label">Active On</span>
								<span className="mobile-metric-value">
									{user.isOnline && user.currentPlatform
										? user.currentPlatform === "macos" ? "macOS"
											: user.currentPlatform.charAt(0).toUpperCase() + user.currentPlatform.slice(1)
										: "-"}
								</span>
							</div>
							<div className="mobile-metric">
								<span className="mobile-metric-label">Platforms</span>
								<span className="mobile-metric-value">
									{user.platformsUsed && user.platformsUsed.length > 0
										? user.platformsUsed.map(p =>
											p === "macos" ? "macOS" : p.charAt(0).toUpperCase() + p.slice(1)
										).join(", ")
										: "-"}
								</span>
							</div>
							<div className="mobile-metric">
								<span className="mobile-metric-label">App Version</span>
								<span className="mobile-metric-value">
									{user.currentPlatform === "web" ? "N/A" : user.appVersion || "-"}
								</span>
							</div>
							<div className="mobile-metric">
								<span className="mobile-metric-label">Last Seen</span>
								<span className="mobile-metric-value">{user.isOnline ? "Now" : formatDate(user.lastSeenAt)}</span>
							</div>
							<div className="mobile-metric">
								<span className="mobile-metric-label">Cabin</span>
								<span className={`mobile-metric-value ${
									user.cabinAssigned === "Yes" ? "cabin-yes" : 
									user.cabinAssigned === "No" ? "cabin-no" : "cabin-na"
								}`}>{user.cabinAssigned}</span>
							</div>
							<div className="mobile-metric">
								<span className="mobile-metric-label">7-Day</span>
								<span className={`mobile-metric-value ${
									parseFloat(user.d7Retention) >= 71.4 ? "high" :
									parseFloat(user.d7Retention) >= 42.8 ? "medium" :
									parseFloat(user.d7Retention) > 0 ? "low" : "inactive"
								}`}>{user.d7Retention}</span>
							</div>
						</div>
					</div>
				))}
			</div>
			<div className="engagement-charts-section">
				{/* Active Users (Last 24 Hours) Chart */}
				<div className="chart-card full-width">
					<div className="chart-header">
						<h3>Active Users (Last 24 Hours)</h3>
						<div className="chart-insights">
							<div className="time-range-selector">
								<button
									className={
										timeInterval === "5min" ? "active" : ""
									}
									onClick={() => setTimeInterval("5min")}
								>
									5M
								</button>
								<button
									className={
										timeInterval === "15min" ? "active" : ""
									}
									onClick={() => setTimeInterval("15min")}
								>
									15M
								</button>
								<button
									className={
										timeInterval === "30min" ? "active" : ""
									}
									onClick={() => setTimeInterval("30min")}
								>
									30M
								</button>
								<button
									className={
										timeInterval === "1hour" ? "active" : ""
									}
									onClick={() => setTimeInterval("1hour")}
								>
									1H
								</button>
							</div>
							<div className="insight-item">
								<span className="insight-label">Avg Active</span>
								<span className="insight-value">
									{hourlyData.length > 0 ? formatDetailedNumber(
										Math.floor(
											hourlyData.reduce(
												(sum, hour) =>
													sum + hour.activeUsers,
												0,
											) / hourlyData.length,
										),
									) : 0}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">Peak Active</span>
								<span className="insight-value">
									{hourlyData.length > 0 ? formatDetailedNumber(
										Math.max(
											...hourlyData.map(
												(h) => h.activeUsers,
											),
										),
									) : 0}
								</span>
							</div>
						</div>
					</div>
					{loadingHourlyData ? (
						<div 
							className="chart-container hourly-active-users-chart"
							style={{
								position: "relative",
								width: "100%",
								height: "340px", // 300 + 40 to match the chart height
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
								paddingBottom: "20px",
							}}
						>
							<div className="loading">Loading hourly data...</div>
						</div>
					) : (
						<HourlyActiveUsersChart data={hourlyData} />
					)}
				</div>

				{/* Daily Interactions Chart */}
				<div className="chart-card full-width">
					<div className="chart-header">
						<h3>Daily Interactions</h3>
						<div className="chart-insights">
							<div className="time-range-selector">
								<button
									className={timeRange === "all-time" ? "active" : ""}
									onClick={() => setTimeRange("all-time")}
								>
									All time
								</button>
								<button
									className={timeRange === "this-month" ? "active" : ""}
									onClick={() => setTimeRange("this-month")}
								>
									This month
								</button>
								<button
									className={timeRange === "last-month" ? "active" : ""}
									onClick={() => setTimeRange("last-month")}
								>
									Last Month
								</button>
							</div>
							<div className="insight-item">
								<span className="insight-label">Avg. DMs/day</span>
								<span className="insight-value">
									{dailyInteractionData && dailyInteractionData.length > 0
										? Math.round(
												dailyInteractionData.reduce(
													(sum, day) => sum + day.dms,
													0,
												) / dailyInteractionData.length,
											)
										: 0}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">
									Avg. Knocks/day
								</span>
								<span className="insight-value">
									{dailyInteractionData && dailyInteractionData.length > 0
										? Math.round(
												(dailyInteractionData.reduce(
													(sum, day) => sum + (day.knocks || 0),
													0,
												) / dailyInteractionData.length) * 10
											) / 10 // One decimal place
										: 0}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">
									Avg. Invites/day
								</span>
								<span className="insight-value">
									{dailyInteractionData && dailyInteractionData.length > 0
										? Math.round(
												(dailyInteractionData.reduce(
													(sum, day) => sum + (day.invites || 0),
													0,
												) / dailyInteractionData.length) * 10
											) / 10 // One decimal place
										: 0}
								</span>
							</div>
						</div>
					</div>
					{loadingEngagement ? (
						<div 
							className="chart-container daily-interaction-chart"
							style={{
								position: "relative",
								width: "100%",
								height: "470px", // 400 + 70 to match the chart height
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
								paddingBottom: "20px",
							}}
						>
							<div className="loading">Loading chart data...</div>
						</div>
					) : dailyInteractionData && dailyInteractionData.length > 0 ? (
						<DailyInteractionChart data={dailyInteractionData} height={400} />
					) : (
						<div 
							className="chart-container daily-interaction-chart"
							style={{
								position: "relative",
								width: "100%",
								height: "470px", // 400 + 70 to match the chart height
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
								paddingBottom: "20px",
							}}
						>
							<div className="no-data">
								No interaction data available for the selected time range
							</div>
						</div>
					)}
				</div>

				{/* Daily Transcripts Chart */}
				<div className="chart-card full-width">
					<div className="chart-header">
						<h3>Daily Transcripts</h3>
						<div className="chart-insights">
							<div className="time-range-selector">
								<button
									className={transcriptRange === "all-time" ? "active" : ""}
									onClick={() => setTranscriptRange("all-time")}
								>
									All time
								</button>
								<button
									className={transcriptRange === "this-month" ? "active" : ""}
									onClick={() => setTranscriptRange("this-month")}
								>
									This month
								</button>
								<button
									className={transcriptRange === "last-month" ? "active" : ""}
									onClick={() => setTranscriptRange("last-month")}
								>
									Last Month
								</button>
							</div>
							<div className="insight-item">
								<span className="insight-label">Avg. Transcripts/day</span>
								<span className="insight-value">
									{transcriptData && transcriptData.length > 0
										? Math.round(
												(transcriptData.reduce(
													(sum, day) => sum + (day.transcripts || 0),
													0,
												) / transcriptData.length) * 10
											) / 10
										: 0}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">Total Transcripts</span>
								<span className="insight-value">
									{transcriptData && transcriptData.length > 0
										? transcriptData.reduce(
												(sum, day) => sum + (day.transcripts || 0),
												0,
											)
										: 0}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">Total Duration</span>
								<span className="insight-value">
									{transcriptData && transcriptData.length > 0
										? formatMinutes(
												transcriptData.reduce(
													(sum, day) => sum + (day.durationMinutes || 0),
													0,
												),
											)
										: "0m"}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">Total Words</span>
								<span className="insight-value">
									{transcriptData && transcriptData.length > 0
										? transcriptData
												.reduce(
													(sum, day) => sum + (day.words || 0),
													0,
												)
												.toLocaleString()
										: 0}
								</span>
							</div>
						</div>
					</div>
					{loadingTranscripts ? (
						<div
							className="chart-container daily-interaction-chart"
							style={{
								position: "relative",
								width: "100%",
								height: "470px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
								paddingBottom: "20px",
							}}
						>
							<div className="loading">Loading chart data...</div>
						</div>
					) : transcriptData && transcriptData.length > 0 ? (
						<DailyInteractionChart
							data={transcriptData}
							height={400}
							metrics={[{ key: "transcripts", label: "Transcripts", color: "#9F7AEA" }]}
							processRow={(item) => item}
							extraTooltipRows={(day) => [
								{ label: "Duration", value: formatMinutes(day.durationMinutes || 0) },
								{ label: "Words", value: (day.words || 0).toLocaleString() },
							]}
						/>
					) : (
						<div
							className="chart-container daily-interaction-chart"
							style={{
								position: "relative",
								width: "100%",
								height: "470px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
								paddingBottom: "20px",
							}}
						>
							<div className="no-data">
								No transcript data available for the selected time range
							</div>
						</div>
					)}
				</div>

				{/* Daily Copilot Responses Chart */}
				<div className="chart-card full-width">
					<div className="chart-header">
						<h3>Daily Copilot Responses</h3>
						<div className="chart-insights">
							<div className="time-range-selector">
								<button
									className={copilotRange === "all-time" ? "active" : ""}
									onClick={() => setCopilotRange("all-time")}
								>
									All time
								</button>
								<button
									className={copilotRange === "this-month" ? "active" : ""}
									onClick={() => setCopilotRange("this-month")}
								>
									This month
								</button>
								<button
									className={copilotRange === "last-month" ? "active" : ""}
									onClick={() => setCopilotRange("last-month")}
								>
									Last Month
								</button>
							</div>
							<div className="insight-item">
								<span className="insight-label">Avg. Responses/day</span>
								<span className="insight-value">
									{copilotData && copilotData.length > 0
										? Math.round(
												(copilotData.reduce(
													(sum, day) => sum + (day.copilotResponses || 0),
													0,
												) / copilotData.length) * 10
											) / 10
										: 0}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">Total Copilot Responses</span>
								<span className="insight-value">
									{copilotData && copilotData.length > 0
										? copilotData
												.reduce(
													(sum, day) => sum + (day.copilotResponses || 0),
													0,
												)
												.toLocaleString()
										: 0}
								</span>
							</div>
						</div>
					</div>
					{loadingCopilot ? (
						<div
							className="chart-container daily-interaction-chart"
							style={{
								position: "relative",
								width: "100%",
								height: "470px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
								paddingBottom: "20px",
							}}
						>
							<div className="loading">Loading chart data...</div>
						</div>
					) : copilotData && copilotData.length > 0 ? (
						<DailyInteractionChart
							data={copilotData}
							height={400}
							metrics={[{ key: "copilotResponses", label: "Copilot Responses", color: "#38B2AC" }]}
							processRow={(item) => item}
						/>
					) : (
						<div
							className="chart-container daily-interaction-chart"
							style={{
								position: "relative",
								width: "100%",
								height: "470px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
								paddingBottom: "20px",
							}}
						>
							<div className="no-data">
								No copilot response data available for the selected time range
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default WorkspaceUsers;
